import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
   BarChart,
   Bar,
   XAxis,
   YAxis,
   CartesianGrid,
   Tooltip,
   ResponsiveContainer,
   Cell,
} from 'recharts';
import styled from 'styled-components';

import Loading from '../../../components/Loading';
import PaginationBar from '../../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../../components/SectionMeta';
import ServiceAquisitionProviderPickerModal from '../ServiceAquisitionProviderPickerModal';
import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../styles/layout/ActionBar';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { TableWrapper } from '../../../styles/table/TableWrapper';
import { Table } from '../../../styles/table/Table';
import * as XLSX from 'xlsx';
import axios from '../../../services/axios';

// ─── Estilos locais ───────────────────────────────────────────────────────────

const ChartsGrid = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 24px;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const ChartContainer = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const ChartTitle = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#444'};
   margin: 0;
`;

const ProviderFilterWrapper = styled.div`
   display: flex;
   gap: 8px;
   align-items: flex-end;
`;

const ProviderFilterDisplay = styled.input`
   flex: 1;
`;

// ─── Constantes ───────────────────────────────────────────────────────────────

const SERVICE_CATEGORY_LABELS = {
   SOFTWARE: 'Software',
   ECOMMERCE: 'E-commerce',
   SERVICO_FISICO: 'Serviço Físico',
   CONSULTORIA: 'Consultoria',
   MARKETING_DIGITAL: 'Marketing Digital',
   OUTROS: 'Outros',
};

const SERVICE_CATEGORY_ORDER = [
   'SOFTWARE',
   'ECOMMERCE',
   'SERVICO_FISICO',
   'CONSULTORIA',
   'MARKETING_DIGITAL',
   'OUTROS',
];
const CHART_BAR_COLORS = [
   '#1976d2',
   '#388e3c',
   '#f57c00',
   '#7b1fa2',
   '#0097a7',
   '#c62828',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value) {
   return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

function formatLegalEntity(legalEntity) {
   const labels = {
      MYSHOP_COMERCIO: 'MyShop Comércio',
      LIFE_PURIFICADORES: 'Life Purificadores',
   };
   return labels[legalEntity] ?? legalEntity;
}

function CurrencyTooltip({ active, payload, label }) {
   if (!active || !payload?.length) return null;
   return (
      <div
         style={{
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 12,
         }}
      >
         <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
         <p style={{ margin: 0, color: '#1976d2' }}>
            {formatCurrency(payload[0].value)}
         </p>
      </div>
   );
}

function ServiceAquisitionDashboard({ summaryData }) {
   const chartData = SERVICE_CATEGORY_ORDER.map((categoryKey) => {
      const categoryRow = summaryData.by_category.find(
         (row) => row.service_category === categoryKey
      );
      return {
         name: SERVICE_CATEGORY_LABELS[categoryKey],
         records_count: categoryRow?.records_count ?? 0,
         total_amount_sum: categoryRow?.total_amount_sum ?? 0,
      };
   });

   return (
      <ChartsGrid>
         <ChartContainer>
            <ChartTitle>Registros por categoria</ChartTitle>
            <ResponsiveContainer width="100%" height={220}>
               <BarChart
                  data={chartData}
                  margin={{ top: 4, right: 8, left: -16, bottom: 40 }}
               >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                     dataKey="name"
                     tick={{ fontSize: 11 }}
                     angle={-30}
                     textAnchor="end"
                     interval={0}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip formatter={(value) => [value, 'Registros']} />
                  <Bar dataKey="records_count" radius={[4, 4, 0, 0]}>
                     {chartData.map((entry, index) => (
                        <Cell
                           key={entry.name}
                           fill={
                              CHART_BAR_COLORS[index % CHART_BAR_COLORS.length]
                           }
                        />
                     ))}
                  </Bar>
               </BarChart>
            </ResponsiveContainer>
         </ChartContainer>

         <ChartContainer>
            <ChartTitle>Valor total por categoria (R$)</ChartTitle>
            <ResponsiveContainer width="100%" height={220}>
               <BarChart
                  data={chartData}
                  margin={{ top: 4, right: 8, left: 16, bottom: 40 }}
               >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                     dataKey="name"
                     tick={{ fontSize: 11 }}
                     angle={-30}
                     textAnchor="end"
                     interval={0}
                  />
                  <YAxis
                     tick={{ fontSize: 11 }}
                     tickFormatter={(value) =>
                        value >= 1000
                           ? `R$ ${(value / 1000).toFixed(0)}k`
                           : `R$ ${value}`
                     }
                  />
                  <Tooltip content={<CurrencyTooltip />} />
                  <Bar dataKey="total_amount_sum" radius={[4, 4, 0, 0]}>
                     {chartData.map((entry, index) => (
                        <Cell
                           key={entry.name}
                           fill={
                              CHART_BAR_COLORS[index % CHART_BAR_COLORS.length]
                           }
                        />
                     ))}
                  </Bar>
               </BarChart>
            </ResponsiveContainer>
         </ChartContainer>
      </ChartsGrid>
   );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ServiceAquisitionList() {
   const navigate = useNavigate();

   // ─── Estado dos filtros ──────────────────────────────────────────────────
   const [filterLegalEntity, setFilterLegalEntity] = useState('');
   const [filterServiceCategory, setFilterServiceCategory] = useState('');
   const [filterReferencePeriod, setFilterReferencePeriod] = useState('');
   const [filterProvider, setFilterProvider] = useState(null); // objeto completo do provider
   const [filterCreatedAtFrom, setFilterCreatedAtFrom] = useState('');
   const [filterCreatedAtTo, setFilterCreatedAtTo] = useState('');
   const [filterIsFinanceValidated, setFilterIsFinanceValidated] = useState('');
   const [filterIsTaxValidated, setFilterIsTaxValidated] = useState('');

   // ─── Estado dos resultados ───────────────────────────────────────────────
   const [serviceAquisitions, setServiceAquisitions] = useState([]);
   const [summaryData, setSummaryData] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);

   const ITEMS_PER_PAGE = 20;

   // ─── Monta params de filtro ───────────────────────────────────────────────
   const buildFilterParams = useCallback(() => {
      const params = {};
      if (filterLegalEntity) params.legal_entity = filterLegalEntity;
      if (filterServiceCategory)
         params.service_category = filterServiceCategory;
      if (filterReferencePeriod)
         params.reference_period = filterReferencePeriod;
      if (filterProvider)
         params.service_aquisition_provider_id = filterProvider.id;
      if (filterCreatedAtFrom) params.created_at_from = filterCreatedAtFrom;
      if (filterCreatedAtTo) params.created_at_to = filterCreatedAtTo;
      if (filterIsFinanceValidated !== '')
         params.is_finance_validated = filterIsFinanceValidated;
      if (filterIsTaxValidated !== '')
         params.is_tax_validated = filterIsTaxValidated;
      return params;
   }, [
      filterLegalEntity,
      filterServiceCategory,
      filterReferencePeriod,
      filterProvider,
      filterCreatedAtFrom,
      filterCreatedAtTo,
      filterIsFinanceValidated,
      filterIsTaxValidated,
   ]);

   // ─── Busca index e summary em paralelo ───────────────────────────────────
   const fetchServiceAquisitions = useCallback(
      async (pageToFetch = 1) => {
         setIsLoading(true);
         try {
            const filterParams = buildFilterParams();

            const [indexResponse, summaryResponse] = await Promise.all([
               axios.get('/service-aquisition', {
                  params: {
                     ...filterParams,
                     page: pageToFetch,
                     limit: ITEMS_PER_PAGE,
                  },
               }),
               axios.get('/service-aquisition/summary', {
                  params: filterParams,
               }),
            ]);

            setServiceAquisitions(indexResponse.data.data.serviceAquisitions);
            setTotalPages(indexResponse.data.meta.total_pages);
            setCurrentPage(pageToFetch);
            setSummaryData(summaryResponse.data.data);
         } catch (err) {
            const errorMessage =
               err.response?.data?.errors?.[0]?.message ??
               err.response?.data?.message ??
               'Erro ao buscar registros';
            toast.error(errorMessage);
         } finally {
            setIsLoading(false);
         }
      },
      [buildFilterParams]
   );

   function handleSearchSubmit(event) {
      event.preventDefault();
      fetchServiceAquisitions(1);
   }

   function handleClearFilters() {
      setFilterLegalEntity('');
      setFilterServiceCategory('');
      setFilterReferencePeriod('');
      setFilterProvider(null);
      setFilterCreatedAtFrom('');
      setFilterCreatedAtTo('');
      setFilterIsFinanceValidated('');
      setFilterIsTaxValidated('');
      setServiceAquisitions([]);
      setSummaryData(null);
      setTotalPages(1);
      setCurrentPage(1);
   }

   const hasResults = serviceAquisitions.length > 0;

   // ─── Exportação XLSX ─────────────────────────────────────────────────────
   const [isExporting, setIsExporting] = useState(false);

   async function handleExport() {
      setIsExporting(true);
      try {
         const filterParams = buildFilterParams();

         const response = await axios.get('/service-aquisition', {
            params: { ...filterParams, page: 1, limit: 9999 },
         });

         const allRecords = response.data.data.serviceAquisitions;

         if (!allRecords.length) {
            toast.warn('Nenhum registro para exportar');
            return;
         }

         // Monta as linhas no formato de colunas definido
         const worksheetRows = allRecords.map((record) => ({
            ID: record.id,
            Entidade: formatLegalEntity(record.legal_entity),
            Prestador: record.provider?.provider_name ?? '—',
            CNPJ: record.provider?.cnpj_number ?? '—',
            Categoria:
               SERVICE_CATEGORY_LABELS[record.service_category] ??
               record.service_category,
            'Nº NFS-e': record.nfse.number ?? '—',
            'Período Ref.': record.reference_period,
            'Data Emissão': record.document_generation_date ?? '—',
            'Valor Total': Number(record.total_amount),
            'Tem Arquivo': record.nfse.has_file ? 'Sim' : 'Não',
            'Registrado por': record.created_by_user?.nome ?? '—',
            'Criado em': record.metadata.created_at
               ? new Date(record.metadata.created_at).toLocaleString('pt-BR')
               : '—',
         }));

         const worksheet = XLSX.utils.json_to_sheet(worksheetRows);
         const workbook = XLSX.utils.book_new();

         // Ajusta largura das colunas automaticamente
         const columnWidths = [
            { wch: 6 }, // ID
            { wch: 18 }, // Entidade
            { wch: 30 }, // Prestador
            { wch: 20 }, // CNPJ
            { wch: 18 }, // Categoria
            { wch: 14 }, // Nº NFS-e
            { wch: 14 }, // Período Ref.
            { wch: 14 }, // Data Emissão
            { wch: 14 }, // Valor Total
            { wch: 12 }, // Tem Arquivo
            { wch: 22 }, // Registrado por
            { wch: 20 }, // Criado em
         ];
         worksheet['!cols'] = columnWidths;

         XLSX.utils.book_append_sheet(workbook, worksheet, 'Serviços Tomados');

         const exportFileName = `servicos_tomados_${new Date().toISOString().substring(0, 10)}.xlsx`;
         XLSX.writeFile(workbook, exportFileName);

         toast.success(`${allRecords.length} registros exportados com sucesso`);
      } catch (err) {
         const errorMessage =
            err.response?.data?.message ?? 'Erro ao exportar registros';
         toast.error(errorMessage);
      } finally {
         setIsExporting(false);
      }
   }

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <ServiceAquisitionProviderPickerModal
            isOpen={isProviderModalOpen}
            onSelect={(provider) => setFilterProvider(provider)}
            onClose={() => setIsProviderModalOpen(false)}
         />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Serviços Tomados</PageTitle>
               <PageSubtitle>
                  Gestão de tomada de serviços e NFS-e recebidas
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <button
                  type="button"
                  onClick={() => navigate('/finance/service-aquisition/new')}
               >
                  + Novo Registro
               </button>
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros ──────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearchSubmit}>
                  <FormGrid>
                     <Field className="col-3">
                        <label htmlFor="filter-legal-entity">
                           Entidade Jurídica
                        </label>
                        <select
                           id="filter-legal-entity"
                           value={filterLegalEntity}
                           onChange={(e) =>
                              setFilterLegalEntity(e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="MYSHOP_COMERCIO">
                              MyShop Comércio
                           </option>
                           <option value="LIFE_PURIFICADORES">
                              Life Purificadores
                           </option>
                        </select>
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-service-category">
                           Categoria
                        </label>
                        <select
                           id="filter-service-category"
                           value={filterServiceCategory}
                           onChange={(e) =>
                              setFilterServiceCategory(e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="SOFTWARE">Software</option>
                           <option value="ECOMMERCE">E-commerce</option>
                           <option value="SERVICO_FISICO">
                              Serviço Físico
                           </option>
                           <option value="CONSULTORIA">Consultoria</option>
                           <option value="MARKETING_DIGITAL">
                              Marketing Digital
                           </option>
                           <option value="OUTROS">Outros</option>
                        </select>
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-reference-period">
                           Período de Referência
                        </label>
                        <input
                           id="filter-reference-period"
                           type="text"
                           placeholder="Ex: 03/2026"
                           maxLength={7}
                           value={filterReferencePeriod}
                           onChange={(e) =>
                              setFilterReferencePeriod(e.target.value)
                           }
                        />
                     </Field>

                     {/* ─── Filtro por prestador via modal ───────────────────── */}
                     <Field className="col-3">
                        <label>Prestador</label>
                        <ProviderFilterWrapper>
                           <ProviderFilterDisplay
                              type="text"
                              value={
                                 filterProvider
                                    ? filterProvider.provider_name
                                    : ''
                              }
                              placeholder="Todos os prestadores"
                              disabled
                              readOnly
                           />
                           {filterProvider ? (
                              <button
                                 type="button"
                                 onClick={() => setFilterProvider(null)}
                                 title="Limpar prestador"
                              >
                                 ✕
                              </button>
                           ) : (
                              <button
                                 type="button"
                                 onClick={() => setIsProviderModalOpen(true)}
                              >
                                 Pesquisar
                              </button>
                           )}
                        </ProviderFilterWrapper>
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-created-at-from">
                           Cadastrado de
                        </label>
                        <input
                           id="filter-created-at-from"
                           type="date"
                           value={filterCreatedAtFrom}
                           onChange={(e) =>
                              setFilterCreatedAtFrom(e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-created-at-to">
                           Cadastrado até
                        </label>
                        <input
                           id="filter-created-at-to"
                           type="date"
                           value={filterCreatedAtTo}
                           onChange={(e) =>
                              setFilterCreatedAtTo(e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-is-finance-validated">
                           Validação Financeira
                        </label>
                        <select
                           id="filter-is-finance-validated"
                           value={filterIsFinanceValidated}
                           onChange={(e) =>
                              setFilterIsFinanceValidated(e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="true">Validado</option>
                           <option value="false">Pendente</option>
                        </select>
                     </Field>

                     <Field className="col-3">
                        <label htmlFor="filter-is-tax-validated">
                           Validação Contábil
                        </label>
                        <select
                           id="filter-is-tax-validated"
                           value={filterIsTaxValidated}
                           onChange={(e) =>
                              setFilterIsTaxValidated(e.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           <option value="true">Validado</option>
                           <option value="false">Pendente</option>
                        </select>
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <button
                           type="button"
                           onClick={handleClearFilters}
                           disabled={isLoading}
                        >
                           Limpar
                        </button>
                        <button type="submit" disabled={isLoading}>
                           Pesquisar
                        </button>
                        {hasResults && (
                           <button
                              type="button"
                              onClick={handleExport}
                              disabled={isLoading || isExporting}
                           >
                              {isExporting ? 'Exportando...' : 'Exportar XLSX'}
                           </button>
                        )}
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Mini dashboard ───────────────────────────────────────────── */}
         {summaryData && (
            <Section>
               <SectionHeader>
                  <SectionTitle>Visão por Categoria</SectionTitle>
               </SectionHeader>
               <SectionBody>
                  <ServiceAquisitionDashboard summaryData={summaryData} />
               </SectionBody>
            </Section>
         )}

         {/* ─── Resultados ───────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionHeaderRow>
                  <SectionTitle>Resultados</SectionTitle>
                  {summaryData && (
                     <SectionMeta
                        items={[
                           {
                              key: 'total_records',
                              label: 'Total de registros:',
                              value: summaryData.total_records,
                           },
                           {
                              key: 'total_amount',
                              label: 'Total em R$:',
                              value: formatCurrency(
                                 summaryData.total_amount_sum
                              ),
                           },
                        ]}
                     />
                  )}
               </SectionHeaderRow>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>ID</th>
                                 <th>Entidade</th>
                                 <th>Prestador</th>
                                 <th>Categoria</th>
                                 <th>Nº NFS-e</th>
                                 <th>Período Ref.</th>
                                 <th className="center">Valor Total</th>
                                 <th className="center">Arquivo</th>
                                 <th>Registrado por</th>
                              </tr>
                           </thead>
                           <tbody>
                              {serviceAquisitions.map((serviceAquisition) => (
                                 <tr
                                    key={serviceAquisition.id}
                                    onClick={() =>
                                       navigate(
                                          `/finance/service-aquisition/${serviceAquisition.id}`
                                       )
                                    }
                                 >
                                    <td>{serviceAquisition.id}</td>
                                    <td>
                                       {formatLegalEntity(
                                          serviceAquisition.legal_entity
                                       )}
                                    </td>
                                    <td>
                                       {serviceAquisition.provider
                                          ?.provider_name ?? '—'}
                                    </td>
                                    <td>
                                       {SERVICE_CATEGORY_LABELS[
                                          serviceAquisition.service_category
                                       ] ?? serviceAquisition.service_category}
                                    </td>
                                    <td>
                                       {serviceAquisition.nfse.number ?? '—'}
                                    </td>
                                    <td>
                                       {serviceAquisition.reference_period}
                                    </td>
                                    <td className="center">
                                       {formatCurrency(
                                          serviceAquisition.total_amount
                                       )}
                                    </td>
                                    <td className="center">
                                       {serviceAquisition.nfse.has_file
                                          ? '✓'
                                          : '—'}
                                    </td>
                                    <td>
                                       {serviceAquisition.created_by_user
                                          ?.nome ?? '—'}
                                    </td>
                                 </tr>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => fetchServiceAquisitions(page)}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum registro encontrado"
                     description="Use os filtros acima e clique em Pesquisar."
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
